// The Pipeline's grouping (app-feel plan, builder B, 2026-10-01).
//
// One rule for the desktop board (phases over stage columns) and the phone
// list (phase headings, stage headings, cards full width) — so the two never
// disagree about who sits where, in which order, or what is folded away.
import type { PipelineClient } from '@/lib/api';

export const DELIVERED_SHOWN = 12;
/** Leads with nothing for this long fold under « + N plus anciens » (owner: dead leads must not fill the board). */
export const LEAD_FRESH_DAYS = 30;
const FOLDED = new Set(['new_lead', 'talking', 'delivered']);

const ms = (iso: string | null | undefined) => Date.parse(String(iso ?? '')) || 0;

export interface StageGroup {
  key: string;
  /** Everyone at this stage. */
  list: PipelineClient[];
  /** What is drawn: the list, minus folded old leads / older deliveries. */
  shown: PipelineClient[];
  count: number;
  money: number;
}
export interface PhaseGroup { key: string; label: string; count: number; stages: StageGroup[] }

export function groupPipeline(
  clients: PipelineClient[],
  phases: Array<{ key: string; label: string; stages: string[] }>,
  opts: { nowMs?: number; unfolded?: Set<string>; searching?: boolean } = {},
): { phases: PhaseGroup[]; offBoard: PipelineClient[] } {
  const nowMs = opts.nowMs ?? Date.now();
  const byStage = new Map<string, PipelineClient[]>();
  const offBoard: PipelineClient[] = [];
  for (const c of clients) {
    if (!c.stage) { offBoard.push(c); continue; }
    const arr = byStage.get(c.stage) ?? [];
    arr.push(c);
    byStage.set(c.stage, arr);
  }
  // Stuck first, then the longest waiting; delivered newest first.
  for (const [stage, arr] of byStage) {
    if (stage === 'delivered') arr.sort((a, b) => ms(b.sinceIso) - ms(a.sinceIso));
    else arr.sort((a, b) => Number(b.stuck) - Number(a.stuck) || ms(a.sinceIso) - ms(b.sinceIso));
  }
  const cutoff = nowMs - LEAD_FRESH_DAYS * 86_400_000;
  const out: PhaseGroup[] = phases.map((p) => {
    const stages = p.stages.map((s): StageGroup => {
      const list = byStage.get(s) ?? [];
      const shown = !FOLDED.has(s) || opts.unfolded?.has(s) || opts.searching ? list
        : s === 'delivered' ? list.slice(0, DELIVERED_SHOWN)
          : list.filter((c) => ms(c.lastActivityIso) >= cutoff);
      return { key: s, list, shown, count: list.length, money: list.reduce((n, c) => n + c.money, 0) };
    });
    return { key: p.key, label: p.label, count: stages.reduce((n, s) => n + s.count, 0), stages };
  });
  return { phases: out, offBoard };
}
